import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { usePageTitle } from "@/contexts/PageTitleContext";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { getStoredUser } from "@/lib/auth";
import { api } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

import LeadTableColumnsDialog from "@/components/leads/LeadTableColumnsDialog";
import CustomViewsDialog from "@/components/leads/CustomViewsDialog";
import InCardFilterPanel from "@/components/leads/InCardFilterPanel";

import ImportCommissionProfilesCsvDialog from "@/components/commission/ImportCommissionProfilesCsvDialog";
import EditCommissionProfileDialog from "@/components/commission/EditCommissionProfileDialog";
import CommissionProfileDialog from "@/components/commission/CommissionProfileDialog";


import { ArrowUpDown, ChevronDown, Download, ListFilter, Plus, Search, Settings2, Trash2, BadgeDollarSign } from "lucide-react";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";



function prettifyCommissionFieldLabel(key) {
  const map = {
    company_name: "Company",
    product_name: "Product",
    issue_age: "Issue Age",
    agent_id: "Agent ID",
    sales_agent: "Sales Agent",
  effective_start_date: "Profile Start Date",
  effective_end_date: "Profile End Date",
    default_agent_commission_percent: "Agent Commission %",
    house_commission_percent: "House Commission %",
    default_agent_advance_months: "Agent Adv Months",
    house_advance_months: "House Adv Months",
  };
  return map[key] || key;
}

function toCsvCell(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes("\n") || s.includes('"')) {
    return '"' + s.replaceAll('"', '""') + '"';
  }
  return s;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map(toCsvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


const CHIP_CLASSES = [
  "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20",
  "bg-emerald-500/20 text-emerald-100 ring-emerald-400/20",
  "bg-cyan-500/20 text-cyan-100 ring-cyan-400/20",
  "bg-fuchsia-500/20 text-fuchsia-100 ring-fuchsia-400/20",
  "bg-amber-500/20 text-amber-100 ring-amber-400/20",
  "bg-rose-500/20 text-rose-100 ring-rose-400/20",
  "bg-sky-500/20 text-sky-100 ring-sky-400/20",
  "bg-violet-500/20 text-violet-100 ring-violet-400/20",
];

function hashString(s) {
  let h = 0;
  const str = String(s || "");
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

function Chip({ value }) {
  const v = String(value || "—");
  const cls = CHIP_CLASSES[hashString(v) % CHIP_CLASSES.length];
  return (
    <Badge className={`ring-1 max-w-full overflow-hidden whitespace-nowrap ${cls}`}>
      <span className="min-w-0 truncate">{v}</span>
    </Badge>
  );
}

function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const DEFAULT_VISIBLE_FILTERS = [
  "company",
  "product",
  "issue_age",
  "agent_id",
  "sales_agent",
  "agent_commission_percent",
  "house_commission_percent",
  "agent_adv_months",
  "house_adv_months",
];
function getCommissionRowField(row, key, agentsMap = {}) {
  if (!row) return "";

  const agentId = row.agent_id || "";

  if (key === "agent_id") return agentId;
  if (key === "sales_agent") return agentId ? agentsMap[agentId] || agentId : "";

  if (key === "effective_start_date" || key === "effective_end_date") {
    // stored as ISO yyyy-mm-dd
    const raw = row[key];
    if (!raw) return "";
    const s = String(raw).slice(0, 10);
    const [y, m, d] = s.split("-");
    if (!y || !m || !d) return s;
    return `${m}/${d}/${y}`;
  }

  return row[key] ?? "";
}

function renderCommissionCell(row, key, agentsMap) {
  if (key === "company_name") return <Chip value={row.company_name} />;
  if (key === "product_name") return <Chip value={row.product_name} />;
  if (key === "sales_agent") return <Chip value={getCommissionRowField(row, "sales_agent", agentsMap)} />;

  return String(getCommissionRowField(row, key, agentsMap) ?? "");
}


const DEFAULT_TABLE_COLUMNS = [
  "company_name",
  "product_name",
  "issue_age",
  "agent_id",
  "sales_agent",
  "effective_start_date",
  "effective_end_date",
  "default_agent_commission_percent",
  "house_commission_percent",
  "default_agent_advance_months",
  "house_advance_months",
];

const VIEW_LABEL = "All Companies";

const DEFAULT_LIMIT = 10;

export default function CommissionSetupPage() {
  usePageTitle("Commission Profiles");
  const { toast } = useToast();
  const nav = useNavigate();
  const user = getStoredUser();
  
  // P0 TENANT ISOLATION: Subscribe to tenant context for automatic refetch
  const { selectedTenantId } = useTenant();

  const [selected, setSelected] = useState(new Set());


  const isAdmin = user?.role === "admin";
  const isSuper = user?.role === "super_admin";

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);


  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("commission_profiles_filters_open");
      return stored ? stored === "true" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("commission_profiles_filters_open", String(filtersOpen));
    } catch {
      // ignore
    }
  }, [filtersOpen]);

  const [filtersDraft, setFiltersDraft] = useState({});
  const [filtersApplied, setFiltersApplied] = useState({});

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 250);

  const [customViewsOpen, setCustomViewsOpen] = useState(false);
  const [activeCustomViewId, setActiveCustomViewId] = useState("all");

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);




  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefs, setPrefs] = useState({
    visible_filters: DEFAULT_VISIBLE_FILTERS,
    custom_filters: [],
    custom_views: [],
    active_custom_view_id: "all",
    table_columns: DEFAULT_TABLE_COLUMNS,
    all_table_columns: DEFAULT_TABLE_COLUMNS,
  });

  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);


  const customViews = Array.isArray(prefs.custom_views) ? prefs.custom_views : [];
  const allTableColumns = Array.isArray(prefs.all_table_columns) && prefs.all_table_columns.length > 0
    ? prefs.all_table_columns
    : prefs.table_columns;

  const [agentsMap, setAgentsMap] = useState({});

  const tableColumns = activeCustomViewId === "all" ? allTableColumns : prefs.table_columns;

  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState("single"); // single | template | csv

  const [columnsOpen, setColumnsOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get("/commission/profiles");
      setItems(res.data || []);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/user-prefs");
        const next = res.data?.commission_profiles || {};
        setPrefs((prev) => {
          const activeId = typeof next.active_custom_view_id === "string" ? next.active_custom_view_id : prev.active_custom_view_id;
          const allCols = Array.isArray(next.all_table_columns) && next.all_table_columns.length > 0
            ? next.all_table_columns
            : Array.isArray(next.table_columns) && next.table_columns.length > 0
              ? next.table_columns
              : prev.all_table_columns;
          const tableCols = Array.isArray(next.table_columns) && next.table_columns.length > 0 ? next.table_columns : prev.table_columns;
          return {
            ...prev,
            ...next,
            visible_filters: Array.isArray(next.visible_filters) ? next.visible_filters : prev.visible_filters,
            custom_filters: Array.isArray(next.custom_filters) ? next.custom_filters : prev.custom_filters,
            table_columns: activeId === "all" ? allCols : tableCols,
            all_table_columns: allCols,
            custom_views: Array.isArray(next.custom_views) ? next.custom_views : prev.custom_views,
            active_custom_view_id: activeId,
          };
        });
        if (typeof next.page_size === "number") {
          setLimit(next.page_size);
        }
        if (typeof next.active_custom_view_id === "string") {
          setActiveCustomViewId(next.active_custom_view_id);
        }
      } catch (e) {
        // ignore
      } finally {
        setPrefsLoading(false);
      }
    })();
  }, []);

  const savePrefs = async (nextOrUpdater) => {
    // Support both direct value and updater function
    const next = typeof nextOrUpdater === 'function' 
      ? nextOrUpdater(prefs) 
      : nextOrUpdater;
    
    setPrefs(next);
    await api.put("/user-prefs", { commission_profiles: next });
  };

  useEffect(() => {
    if (prefsLoading) return;
    savePrefs((prev) => ({ ...prev, active_custom_view_id: activeCustomViewId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCustomViewId]);

  useEffect(() => {
    if (!user || !(isAdmin || isSuper)) {
      nav("/app", { replace: true });
      return;
    }
    refresh();
    // P0 TENANT ISOLATION: Refetch when tenant context changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAdmin, isSuper, selectedTenantId]);

  useEffect(() => {
    (async () => {
      try {
        // Use full user list for name mapping (profiles may reference any user id).
        const res = await api.get("/admin/users");
        const m = {};
        (res.data || []).forEach((u) => {
          m[u.id] = u.name || u.email || u.id;
        });
        setAgentsMap(m);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    setOffset(0);
  }, [filtersApplied, qDebounced]);


  const filterFields = useMemo(() => {
    return [
      { key: "agent_adv_months", label: "Agent Adv Months", type: "text" },
      { key: "agent_commission_percent", label: "Agent Commission %", type: "text" },
      { key: "agent_id", label: "Agent ID", type: "text" },
      { key: "company", label: "Company", type: "text" },
      { key: "house_adv_months", label: "House Adv Months", type: "text" },
      { key: "house_commission_percent", label: "House Commission %", type: "text" },
      { key: "issue_age", label: "Issue Age", type: "text" },
      { key: "product", label: "Product", type: "text" },
      { key: "sales_agent", label: "Sales Agent", type: "text" },
    ];
  }, []);

  // (sorting state is declared after filtered is computed)

  const optionsByField = useMemo(() => ({}), []);

  const filtered = useMemo(() => {
    const needle = qDebounced.trim().toLowerCase();
    const applied = filtersApplied || {};

    return (items || []).filter((row) => {
      if (needle) {
        const hay = [row.company_name, row.product_name, row.issue_age]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }

      // Lightweight text contains filters (keeps it simple for now)
      const clauses = Object.entries(applied)
        .filter(([, v]) => v?.enabled)
        .map(([k, v]) => [k, v]);

      for (const [k, cfg] of clauses) {
        const op = cfg.operator;
        const val = String(cfg.value || "").trim().toLowerCase();
        if (!val) continue;

        const fieldValue =
        k === "company"
          ? String(row.company_name || "")
          : k === "product"
            ? String(row.product_name || "")
            : k === "issue_age"
              ? String(row.issue_age || "")
              : k === "agent_id"
                ? String(row.agent_id || "")
                : k === "sales_agent"
                  ? String(agentsMap[row.agent_id] || row.agent_id || "")
                  : k === "agent_commission_percent"
                    ? String(row.default_agent_commission_percent ?? "")
                    : k === "house_commission_percent"
                      ? String(row.house_commission_percent ?? "")
                      : k === "agent_adv_months"
                        ? String(row.default_agent_advance_months ?? "")
                        : k === "house_adv_months"
                          ? String(row.house_advance_months ?? "")
                          : "";

        const fv = fieldValue.toLowerCase();
        if (op === "is" && fv !== val) return false;
        if (op === "contains" && !fv.includes(val)) return false;
        if (op === "starts_with" && !fv.startsWith(val)) return false;
        if (op === "ends_with" && !fv.endsWith(val)) return false;
        if (op === "isnt" && fv === val) return false;
      }

      return true;
    });
  }, [items, qDebounced, filtersApplied, agentsMap]);

  const filtersActive = useMemo(() => {
    return Object.values(filtersApplied || {}).some((v) => v?.enabled);
  }, [filtersApplied]);

  const activeViewLabel = useMemo(() => {
    if (activeCustomViewId === "all") return VIEW_LABEL;
    const v = (customViews || []).find((x) => x.id === activeCustomViewId);
    return v?.name || VIEW_LABEL;
  }, [activeCustomViewId, customViews]);

  const clearFilters = () => {
    setFiltersDraft({});
    setFiltersApplied({});
  };

  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const applySort = (field, dir) => {
    setSortBy(field);
    setSortDir(dir);
  };

  const sortedRows = useMemo(() => {
    const rows = Array.isArray(filtered) ? [...filtered] : [];
    if (!sortBy) return rows;
    const mult = sortDir === "desc" ? -1 : 1;
    rows.sort((a, b) => {
      const av = String(getCommissionRowField(a, sortBy, agentsMap) ?? "");
      const bv = String(getCommissionRowField(b, sortBy, agentsMap) ?? "");
      return av.localeCompare(bv, undefined, { sensitivity: "base" }) * mult;
    });
    return rows;
  }, [filtered, sortBy, sortDir, agentsMap]);

  const COL_MIN_PX = 160;
  const tableMinWidthPx = useMemo(() => {
    const colCount = (tableColumns || []).length;
    // checkbox (46) + settings spacer (44) + overhead (~80) + N columns
    return 46 + 44 + 80 + Math.max(colCount, 1) * COL_MIN_PX;
  }, [tableColumns]);

  const pageRows = useMemo(() => {
    return sortedRows.slice(offset, offset + limit);
  }, [sortedRows, offset, limit]);

  const total = sortedRows.length;

  const allOnPageSelected = useMemo(
    () => pageRows.length > 0 && pageRows.every((x) => selected.has(x.id)),
    [pageRows, selected]
  );

  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        pageRows.forEach((x) => next.add(x.id));
      } else {
        pageRows.forEach((x) => next.delete(x.id));
      }
      return next;
    });
  };

  const anySelected = selected.size > 0;

  const selectedRows = useMemo(() => sortedRows.filter((x) => selected.has(x.id)), [sortedRows, selected]);

  const exportSelectedCsv = async () => {
    const fields = tableColumns || [];
    if (fields.length === 0) return;

    if (!anySelected) return;

    const rows = [fields.map((k) => prettifyCommissionFieldLabel(k))];
    selectedRows.forEach((r) => {
      rows.push(fields.map((k) => getCommissionRowField(r, k) ?? ""));
    });

    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`commission_profiles_selected_${stamp}.csv`, rows);
    toast({ title: "Export downloaded" });
  };

  const deleteSelected = async () => {
    try {
      await Promise.all(selectedRows.map((r) => api.delete(`/commission/profiles/${r.id}`)));
      toast({ title: "Deleted" });
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast({ title: "Could not delete", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };


  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-230px)] p-6 gap-4" data-testid="commission-setup-page">

      {/* Controls row on background between title and table */}
      <div className="flex flex-col gap-2 rounded-2xl bg-black/0 px-1 sm:flex-row sm:items-center sm:justify-between shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>

            <DropdownMenuTrigger asChild>

              <Button
                type="button"
                variant="outline"
                data-testid="commission-view"
                aria-label="View"
              >
                {activeViewLabel}
                <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10 text-slate-100">
              <DropdownMenuItem
                onClick={() => {
                  setActiveCustomViewId("all");
                  setFiltersApplied({});
                  setFiltersDraft({});
                  savePrefs((prev) => {
                    const allColumns = Array.isArray(prev.all_table_columns) && prev.all_table_columns.length > 0
                      ? prev.all_table_columns
                      : DEFAULT_TABLE_COLUMNS;
                    return { ...prev, table_columns: allColumns, active_custom_view_id: "all" };
                  });
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="commission-view-all"
              >
                {VIEW_LABEL}
              </DropdownMenuItem>
              {(customViews || []).map((v) => (
                <DropdownMenuItem
                  key={v.id}
                  onClick={() => {
                    setActiveCustomViewId(v.id);
                    setFiltersApplied(v.filters_applied || {});
                    setFiltersDraft(v.filters_applied || {});
                    savePrefs((prev) => {
                      const nextColumns = Array.isArray(v.columns) && v.columns.length > 0 
                        ? v.columns 
                        : prev.table_columns;
                      return { ...prev, table_columns: nextColumns, active_custom_view_id: v.id };
                    });
                  }}
                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                  data-testid={`commission-view-${v.id}`}
                >
                  {v.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => setCustomViewsOpen(true)}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="commission-manage-views"
              >
                Manage Views…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {anySelected ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="commission-actions"
                >
                  Actions
                  <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100">
                <DropdownMenuItem
                  onClick={exportSelectedCsv}
                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                  data-testid="commission-export-selected"
                >
                  <Download className="mr-2 h-4 w-4" /> Export (CSV)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={deleteSelected}
                  className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100"
                  data-testid="commission-delete-selected"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <CollapsibleSearch
            value={q}
            onChange={setQ}
            placeholder="Search commission profiles..."
            width="w-[280px]"
            data-testid="commission-search"
          />

          {/* Filter Button - between search and columns */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              const next = !filtersOpen;
              setFiltersOpen(next);
            }}
            aria-label="Open filters"
            data-testid="commission-filter-icon"
            className="relative"
          >
            <ListFilter className={`h-4 w-4 ${filtersOpen ? "text-indigo-400" : ""}`} />
            {filtersActive ? <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-indigo-400 ring-2 ring-slate-950" /> : null}
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setColumnsOpen(true)}
            aria-label="Customize columns"
            data-testid="commission-customize-columns"
            disabled={prefsLoading}
          >
            <Settings2 className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="commission-add-trigger"
              >
                <Plus className="mr-2 h-4 w-4" /> Commission Profile
                <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10 text-slate-100">
              <DropdownMenuItem
                onClick={() => {
                  setAddMode("single");
                  setAddOpen(true);
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="commission-add-single"
              >
                Single Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setAddMode("template");
                  setAddOpen(true);
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="commission-add-template"
              >
                Use Template
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setAddMode("csv");
                  setAddOpen(true);
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="commission-add-csv"
              >
                Upload CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filter + List cards */}
      <div
        className={
          "grid min-w-0 grid-cols-1 gap-3 flex-1 min-h-0 " +
          (filtersOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1")
        }
      >
        {filtersOpen ? (
          <Card className="glass flex flex-col min-h-0 h-full" data-testid="commission-filter-card">
            <CardContent className="h-full p-0">
              <InCardFilterPanel
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                title="Filter Commission Profiles by"
                fields={filterFields}
                optionsByField={optionsByField}
                draft={filtersDraft}
                onChangeDraft={setFiltersDraft}
                onApply={() => setFiltersApplied(filtersDraft)}
                onClear={clearFilters}
              />
            </CardContent>
          </Card>
        ) : null}

        <Card className="glass flex flex-col min-h-0 h-full min-w-0 w-full" data-testid="commission-list-card">
          <CardContent className="flex h-full flex-col p-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-200/70">
                Loading…
              </div>
            ) : (
            <div data-testid="commission-table-wrap" className="flex-1 min-w-0 overflow-hidden">
              <div
                className="h-full w-full min-w-0 overflow-auto"
                data-testid="commission-hscroll"
                onWheel={(e) => {
                  if (e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.scrollLeft += e.deltaY;
                  }
                }}
              >
                <Table style={{ minWidth: tableMinWidthPx }} containerClassName="h-full">
                  <TableHeader className="relative">
                    <TableRow className="border-transparent hover:bg-transparent">
                      <TableHead colSpan={(tableColumns || []).length + 1} className="relative h-auto p-1 text-slate-200/70">
                        <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-px -translate-y-[11px] bg-white/10" />
                        <div className="w-full px-2 py-3 grid grid-cols-[46px_1fr_44px] items-center gap-3">
                          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={allOnPageSelected}
                              onCheckedChange={toggleAllOnPage}
                              className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                              data-testid="commission-select-all"
                            />
                          </div>

                          <div
                            className="min-w-0 grid items-center gap-3 pr-2"
                            style={{ gridTemplateColumns: `repeat(${(tableColumns || []).length}, minmax(160px, 1fr))` }}
                            data-testid="cp-header-grid"
                          >
                            {(tableColumns || []).map((k) => (
                              <div key={k} className="flex min-w-0 items-center justify-between gap-2 pr-2 text-xs">
                                <span className="min-w-0 flex-1 truncate">{prettifyCommissionFieldLabel(k)}</span>
                                <button
                                  type="button"
                                  className={
                                    "inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" +
                                    (sortBy === k ? " bg-white/10" : "")
                                  }
                                  aria-label={`Sort ${prettifyCommissionFieldLabel(k)}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const nextDir = sortBy === k && sortDir === "asc" ? "desc" : "asc";
                                    applySort(k, nextDir);
                                  }}
                                  data-testid={`commission-sort-${k}`}
                                >
                                  <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                </button>
                              </div>
                            ))}
                          </div>

                          <div className="h-9 w-9" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {sortedRows.length === 0 ? (
                      <TableRow className="border-transparent">
                        <TableCell colSpan={(tableColumns || []).length + 1} className="py-16">
                          <div className="sticky left-1/2 -translate-x-1/2 w-max flex flex-col items-center justify-center">
                            <BadgeDollarSign className="h-12 w-12 text-indigo-400 mb-3" />
                            <div className="text-sm text-slate-200/70">No commission profiles found.</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : pageRows.map((row) => (
                          <TableRow key={row.id} className="border-transparent" data-testid={`commission-row-${row.id}`}> 
                          <TableCell colSpan={(tableColumns || []).length + 1} className="p-1 align-top">
                              <div
                                role="button"
                                tabIndex={0}
                                className="w-full rounded-xl bg-white/5 px-2 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10"
                                onClick={() => {
                                  setEditRow(row);
                                  setEditOpen(true);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setEditRow(row);
                                    setEditOpen(true);
                                  }
                                }}
                                data-testid={`commission-row-button-${row.id}`}
                              >
                                <div className="grid grid-cols-[46px_1fr_44px] items-center gap-3">
                                  <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      checked={selected.has(row.id)}
                                      onCheckedChange={() => toggleOne(row.id)}
                                      className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                                      data-testid={`commission-row-select-${row.id}`}
                                    />
                                  </div>

                                <div className="min-w-0 grid grid-cols-1">
                                  <div
                                    className="grid items-center gap-3 pr-2"
                                    style={{ gridTemplateColumns: `repeat(${(tableColumns || []).length}, minmax(160px, 1fr))` }}
                                    data-testid="cp-row-grid"
                                  >
                                    {(tableColumns || []).map((k) => (
                                      <div key={k} className="min-w-0">
                                        {k === "company_name" || k === "product_name" || k === "sales_agent" ? (
                                          renderCommissionCell(row, k, agentsMap)
                                        ) : (
                                          <div className="truncate text-slate-100/90">{renderCommissionCell(row, k, agentsMap)}</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              </div>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </div>
            </div>
            )}

            <div data-testid="commission-footer" className="h-[60px] border-t border-white/10 px-4 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-200/70" data-testid="commission-count">
                {anySelected ? `Selected: ${selected.size}` : `Matching current filters: ${total}`}
              </div>

              <div className="flex flex-wrap items-center gap-2" data-testid="commission-pagination">
                {offset > 0 ? (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    data-testid="commission-prev"
                  >
                    Previous
                  </Button>
                ) : null}

                {offset + limit < total ? (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={offset + limit >= total}
                    onClick={() => setOffset((o) => o + limit)}
                    data-testid="commission-next"
                  >
                    Next
                  </Button>
                ) : null}

                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    const next = Number(v);
                    setLimit(next);
                    setOffset(0);
                    savePrefs((prev) => ({ ...prev, page_size: next }));
                  }}
                >
                  <SelectTrigger className="h-9 w-[110px] rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20" data-testid="commission-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10">
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`commission-page-size-${n}`}>
                        {n} / Page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {addMode === "single" ? (
        <CommissionProfileDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          title="Add Profile"
          showHouseAdvanceMonths
          existingProfiles={items}
          onSaved={() => {
            setAddOpen(false);
            refresh();
          }}
        />
      ) : null}

      {addMode === "template" ? (
        <CommissionProfileDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          title="Add Profile"
          templatePlaceholder
          showHouseAdvanceMonths
          existingProfiles={items}
          onSaved={() => {
            setAddOpen(false);
            refresh();
          }}
        />
      ) : null}

      {addMode === "csv" ? (
        <ImportCommissionProfilesCsvDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          onImported={() => {
            setAddOpen(false);
            refresh();
          }}
        />
      ) : null}

      <EditCommissionProfileDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        row={editRow}
        existingProfiles={items}
        onSaved={() => {
          setEditOpen(false);
          setEditRow(null);
          refresh();
        }}
      />


      <LeadTableColumnsDialog
        open={columnsOpen}
        onOpenChange={setColumnsOpen}
        fields={filterFields.map((f) => ({
          key:
            f.key === "company"
              ? "company_name"
              : f.key === "product"
                ? "product_name"
                : f.key === "agent_id"
                  ? "agent_id"
                  : f.key === "sales_agent"
                    ? "sales_agent"
                    : f.key === "agent_commission_percent"
                      ? "default_agent_commission_percent"
                      : f.key === "house_commission_percent"
                        ? "house_commission_percent"
                        : f.key === "agent_adv_months"
                          ? "default_agent_advance_months"
                          : f.key === "house_adv_months"
                            ? "house_advance_months"
                            : f.key,
          label: f.label,
        }))}
        value={tableColumns}
        onSave={(nextColumns) => {
          savePrefs((prev) => {
            const nextPrefs = {
              ...prev,
              table_columns: nextColumns,
            };
            if (activeCustomViewId === "all") {
              nextPrefs.all_table_columns = nextColumns;
            }
            return nextPrefs;
          });
        }}
      />

      <CustomViewsDialog
        open={customViewsOpen}
        onOpenChange={setCustomViewsOpen}
        value={customViews}
        currentDraft={{ filters_applied: filtersApplied }}
        currentColumns={tableColumns}
        onSave={(nextViews) => {
          savePrefs((prev) => ({
            ...prev,
            custom_views: nextViews,
            active_custom_view_id: activeCustomViewId,
          }));
        }}
      />
    </div>
  );
}
